import { useMutation } from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import type { CVAnalysis } from "./index";

type CVAnalysisResponse = {
  msg: string;
  analysis: CVAnalysis;
};

export interface FetchLinkedInProfilePayload {
  linkedin_url: string;
}

// API Functions

const fetchLinkedInProfile = async (
  payload: FetchLinkedInProfilePayload,
): Promise<{ analysis: CVAnalysis }> => {
  const { data } = await axiosInstance.post<CVAnalysisResponse>(
    "/profile/linkedin/fetch",
    payload,
  );

  // BE now handles geocoding and returns standardized residency
  return {
    analysis: data.analysis,
  };
};

// Hooks

export const useFetchLinkedInProfile = () => {
  return useMutation(() => ({
    mutationFn: fetchLinkedInProfile,
  }));
};

// Regex pattern for LinkedIn profile URLs
// Matches: https://www.linkedin.com/in/username, https://linkedin.com/in/username, linkedin.com/in/username, etc.
const LINKEDIN_URL_REGEX =
  /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?(\?.*)?$/i;

// Utility function for URL validation
export const isValidLinkedInUrl = (url: string): boolean => {
  try {
    const trimmedUrl = url.trim();

    // First check with regex to ensure format is correct
    if (!LINKEDIN_URL_REGEX.test(trimmedUrl)) {
      return false;
    }

    // Normalize URL - add https:// if missing for URL parsing
    let normalizedUrl = trimmedUrl;
    if (
      !normalizedUrl.startsWith("http://") &&
      !normalizedUrl.startsWith("https://")
    ) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    // Additional validation using URL constructor to ensure it's a valid URL
    const urlObj = new URL(normalizedUrl);
    return (
      (urlObj.hostname === "www.linkedin.com" ||
        urlObj.hostname === "linkedin.com") &&
      urlObj.pathname.match(/^\/in\/[\w-]+\/?$/) !== null
    );
  } catch {
    return false;
  }
};

// Normalize LinkedIn URL
export const normalizeLinkedInUrl = (url: string): string => {
  let normalized = url.trim();
  if (!normalized.startsWith("http://") && !normalized.startsWith("https://")) {
    normalized = `https://${normalized}`;
  }
  return normalized;
};
